import {
    deserializeReservationLogChangeListItem,
    serializeReservationLogChangeListItem,
    ReservationLogChangeListItem,
    ReservationLogChangeListItemDto
} from "./ReservationLogChangeListItem";

export const ReservationLogEntry = Symbol("ReservationLogEntry");
export interface ReservationLogEntry {
    [ReservationLogEntry]: boolean;
}

export const ReservationLogTextEntry = Symbol("ReservationLogTextEntry");
export interface ReservationLogTextEntry extends ReservationLogEntry {
    [ReservationLogTextEntry]: boolean;
    Text: string;
}

export function isReservationLogTextEntry(val: ReservationLogEntry): val is ReservationLogTextEntry {
    return val && (val as ReservationLogTextEntry)[ReservationLogTextEntry] === true;
}

export const ReservationLogChangeListEntry = Symbol("ReservationLogChangeListEntry");
export interface ReservationLogChangeListEntry extends ReservationLogEntry {
    [ReservationLogChangeListEntry]: boolean;
    Changes: ReservationLogChangeListItem[];
}

export function isReservationLogChangeListEntry(val: ReservationLogEntry): val is ReservationLogChangeListEntry {
    return val && (val as ReservationLogChangeListEntry)[ReservationLogChangeListEntry] === true;
}

export interface ReservationLogEntryDto {
    $type: string;
}

export interface ReservationLogTextEntryDto extends ReservationLogEntryDto {
    text: string;
}

export interface ReservationLogChangeListEntryDto extends ReservationLogEntryDto {
    changes: ReservationLogChangeListItemDto[];
}

/**
 * Extensible (de)serialization registry for ReservationLogEntry.
 * Consumers can register custom handlers keyed by DTO $type or a logical kind.
 */
export type ReservationLogEntryDeserializer = (dto: ReservationLogEntryDto) => ReservationLogEntry;
export type ReservationLogEntrySerializer = (entry: ReservationLogEntry) => ReservationLogEntryDto;

const entryDeserializers = new Map<string, ReservationLogEntryDeserializer>();
const entrySerializers = new Map<string, ReservationLogEntrySerializer>();

export function registerReservationLogEntryDeserializer(type: string, fn: ReservationLogEntryDeserializer): void {
    entryDeserializers.set(type, fn);
}
export function registerReservationLogEntrySerializer(type: string, fn: ReservationLogEntrySerializer): void {
    entrySerializers.set(type, fn);
}

export function deserializeReservationLogEntry(dto: ReservationLogEntryDto): ReservationLogEntry {
    if (!dto) {
        return null;
    }

    // Custom handlers first
    const custom = entryDeserializers.get(dto.$type);
    if (custom) {
        return custom(dto);
    }

    // Built-in fallbacks
    switch (dto.$type) {
        case "Text": {
            const textDto = dto as ReservationLogTextEntryDto;

            const textEntry = createReservationLogTextEntry();
            textEntry.Text = textDto.text;

            return textEntry;
        }
        case "ChangeList": {
            const changeListDto = dto as ReservationLogChangeListEntryDto;
            const changeListEntry = createReservationLogChangeListEntry();
            changeListEntry.Changes = (changeListDto.changes ?? []).map(deserializeReservationLogChangeListItem);
            return changeListEntry;
        }
        default: {
            // Preserve unknown types as opaque entries so consumers can render them via custom renderers
            const unknown: any = {
                [ReservationLogEntry]: true,
                $kind: dto.$type,
                $dto: dto
            };
            return unknown;
        }
    }
}

export function serializeReservationLogEntry(entry: ReservationLogEntry): ReservationLogEntryDto {
    // Built-in kinds
    if (isReservationLogTextEntry(entry)) {
        const dto: ReservationLogTextEntryDto = {
            $type: "Text",
            text: (entry as ReservationLogTextEntry).Text
        };
        return dto;
    }
    if (isReservationLogChangeListEntry(entry)) {
        const e = entry as ReservationLogChangeListEntry;
        const dto: ReservationLogChangeListEntryDto = {
            $type: "ChangeList",
            changes: e.Changes.map(serializeReservationLogChangeListItem)
        };
        return dto;
    }

    // Custom kinds by $kind
    const anyEntry: any = entry as any;
    const kind: string | undefined = typeof anyEntry?.$kind === "string" ? anyEntry.$kind : undefined;
    if (kind) {
        const s = entrySerializers.get(kind);
        if (s) {
            return s(entry);
        }
    }
    // Opaque unknown entries can carry original dto
    if (anyEntry?.$dto) {
        return anyEntry.$dto as ReservationLogEntryDto;
    }

    throw new Error("Cannot serialize ReservationLogEntry - unknown kind");
}

export function createReservationLogTextEntry(): ReservationLogTextEntry {
    const logEntry: ReservationLogTextEntry = {
        [ReservationLogEntry]: true,
        [ReservationLogTextEntry]: true,
        Text: ""
    };

    return logEntry;
};

export function createReservationLogChangeListEntry(): ReservationLogChangeListEntry {
    const logEntry: ReservationLogChangeListEntry = {
        [ReservationLogEntry]: true,
        [ReservationLogChangeListEntry]: true,
        Changes: []
    };

    return logEntry;
}

/**
 * Register built-in handlers at module load.
 */
registerReservationLogEntryDeserializer("Text", (dto) => {
    const d = dto as ReservationLogTextEntryDto;
    const e = createReservationLogTextEntry();
    e.Text = d.text;
    return e;
});
registerReservationLogEntryDeserializer("ChangeList", (dto) => {
    const d = dto as ReservationLogChangeListEntryDto;
    const e = createReservationLogChangeListEntry();
    e.Changes = (d.changes ?? []).map(deserializeReservationLogChangeListItem);
    return e;
});

registerReservationLogEntrySerializer("Text", (entry) => {
    if (!isReservationLogTextEntry(entry)) {
        throw new Error("Serializer 'Text' received non-text entry");
    }
    const dto: ReservationLogTextEntryDto = {
        $type: "Text",
        text: (entry as ReservationLogTextEntry).Text
    };
    return dto;
});
registerReservationLogEntrySerializer("ChangeList", (entry) => {
    if (!isReservationLogChangeListEntry(entry)) {
        throw new Error("Serializer 'ChangeList' received non-changeList entry");
    }
    const e = entry as ReservationLogChangeListEntry;
    const dto: ReservationLogChangeListEntryDto = {
        $type: "ChangeList",
        changes: e.Changes.map(serializeReservationLogChangeListItem)
    };
    return dto;
});
